"use client";

import React from "react";
import { cn } from "@/lib/utils";

/**
 * CProgressBar - A progress bar component for showing completion percentage
 *
 * Displays progress with customizable colors, sizes, and optional animation.
 *
 * @example
 * <CProgressBar
 *   value={75}
 *   label="Profile Completion"
 *   showPercentage={true}
 *   color="primary"
 *   size="default"
 *   animated={true}
 * />
 */

export interface CProgressBarProps {
  /** Progress value from 0 to 100 */
  value: number;
  /** Label displayed above the progress bar */
  label?: string;
  /** Whether to show the percentage value */
  showPercentage?: boolean;
  /** Color variant of the progress bar */
  color?: "primary" | "success" | "warning" | "error";
  /** Size variant of the progress bar */
  size?: "sm" | "default" | "lg";
  /** Whether to animate the progress bar */
  animated?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
}

const CProgressBar: React.FC<CProgressBarProps> = ({
  value,
  label,
  showPercentage = false,
  color = "primary",
  size = "default",
  animated = false,
  className,
}) => {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(100, Math.max(0, value));

  // Size classes for the track height
  const sizeClasses = {
    sm: "h-1",
    default: "h-1.5",
    lg: "h-2",
  };

  // Color classes for the progress bar
  const colorClasses = {
    primary: "bg-primary",
    success: "bg-green-500 dark:bg-green-400",
    warning: "bg-amber-500 dark:bg-amber-400",
    error: "bg-red-500 dark:bg-red-400",
  };

  // Track background color
  const trackClasses = "bg-slate-200 dark:bg-slate-700";

  return (
    <div className={cn("w-full", className)}>
      {/* Label and percentage row */}
      {(label || showPercentage) && (
        <div className="flex items-center justify-between mb-1.5">
          {label && (
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {label}
            </span>
          )}
          {showPercentage && (
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {Math.round(clampedValue)}%
            </span>
          )}
        </div>
      )}

      {/* Progress track */}
      <div
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label || "Progress"}
        className={cn(
          "w-full rounded-full overflow-hidden",
          trackClasses,
          sizeClasses[size],
        )}
      >
        {/* Progress bar */}
        <div
          className={cn(
            "h-full rounded-full transition-all duration-300 ease-out",
            colorClasses[color],
            animated && "animate-pulse",
          )}
          style={{ width: `${clampedValue}%` }}
        />
      </div>
    </div>
  );
};

export default CProgressBar;
